import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import StarsIcon from '@mui/icons-material/Stars';
import {
	ClubDpPointsActionSX,
	ClubDpPointsCardFrameSX,
	ClubDpPointsCardHolderFrameSX,
	ClubDpPointsCardHolderSX,
	ClubDpPointsCardImgSX,
	ClubDpPointsCardLabelSX,
	ClubDpPointsCardNumberSX,
	ClubDpPointsCardPointsFrameSX,
	ClubDpPointsCardPointsIconSX,
	ClubDpPointsCardPointsSX,
	ClubDpPointsDescriptionSX,
	ClubDpPointsTitleSX,
} from '@/components/content/Account/styles/clubDpPoints';
import { useClubDpCards } from '@/data/Content/ClubDp';
import { ACCOUNT_TABS } from '@/components/content/Account/parts/Tabs';
import { ClubDpPointsSkeleton } from '@/components/content/Account/parts/Skeletons/ClubDpPointsSkeleton';
import { combineSX } from '@/utils/combineSX';

interface ClubDpPointsProps {
	onTabSelect: (tab: ACCOUNT_TABS) => void;
	onMutateClubDpFlags: () => void;
}

export const ClubDpPoints: React.FC<ClubDpPointsProps> = () => {
	const isMobile = useIsMobile();
	const { cards, loading } = useClubDpCards();
	const [imageLoaded, setImageLoaded] = useState<boolean>(false);

	if (loading) {
		return <ClubDpPointsSkeleton />;
	}

	if (!cards?.puntosDpVale) {
		return null;
	}

	const { customerName, last4Digits, balancePoints } = cards.puntosDpVale;

	const formattedBalancePoints = new Intl.NumberFormat('en-US', {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(parseFloat(balancePoints));

	return (
		<React.Fragment>
			<Typography component={'h1'} sx={ClubDpPointsTitleSX}>
				{'TUS DATOS CLUB DPPUNTOS'}
			</Typography>
			<Stack direction={isMobile ? 'column' : 'row'} spacing={1} marginBottom={3}>
				<Typography sx={ClubDpPointsDescriptionSX}>
					{'Acumula puntos y canjealos en tus compras.'}
				</Typography>
				<Linkable href="https://dpcardpuntos.com/" target="_blank">
					<Typography sx={ClubDpPointsActionSX}>{'Conoce más'}</Typography>
				</Linkable>
			</Stack>

			<Stack direction={isMobile ? 'column' : 'row'} spacing={3}>
				<Stack spacing={2}>
					<Box sx={ClubDpPointsCardFrameSX(isMobile)}>
						{!imageLoaded ? (
							<Skeleton
								sx={{ borderRadius: '4px' }}
								variant="rectangular"
								width={373}
								height={240}
							/>
						) : null}
						<Img
							src="/images/clubdppuntos-card.png"
							alt="Club Dp"
							loading="eager"
							sx={combineSX([ClubDpPointsCardImgSX, { display: imageLoaded ? 'block' : 'none' }])}
							onLoad={() => setImageLoaded(true)}
						/>
						<Typography
							sx={ClubDpPointsCardNumberSX}
						>{`XXXX - XXXX - XXXX - ${last4Digits}`}</Typography>
					</Box>
				</Stack>
				<Stack direction="column" gap={1}>
					<Box sx={ClubDpPointsCardHolderFrameSX(isMobile)}>
						<Stack direction="column" spacing={1}>
							<Typography component={'h2'} sx={ClubDpPointsCardLabelSX}>
								{'Nombre del cliente'}
							</Typography>
							<Typography component={'h3'} sx={ClubDpPointsCardHolderSX}>
								{customerName}
							</Typography>
						</Stack>
						<Linkable href="https://dpcardpuntos.com/" target="_blank">
							<Typography sx={ClubDpPointsActionSX}>{'Actualizar'}</Typography>
						</Linkable>
					</Box>
					<Box sx={ClubDpPointsCardPointsFrameSX(isMobile)}>
						<Typography component={'h2'} sx={ClubDpPointsCardLabelSX}>
							{'Saldo en dppuntos'}
						</Typography>
						<Typography component={'h3'} sx={ClubDpPointsCardPointsSX}>
							<StarsIcon sx={ClubDpPointsCardPointsIconSX} />
							{`${formattedBalancePoints} puntos`}
						</Typography>
					</Box>
				</Stack>
			</Stack>
		</React.Fragment>
	);
};
